import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { services } from '../data/mockData';
import { companyInfo } from '../data/mockData';

export const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  
  const service = services.find(s => s.id === parseInt(serviceId));

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Service Not Found</h2>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </div>
    );
  }

  const serviceDetails = {
    1: {
      fullDescription: "Our professional tree removal services handle trees of any size, from small ornamental trees to massive oaks. We use state-of-the-art equipment including cranes for challenging removals near structures. Every removal is carefully planned to ensure safety and minimize impact on your property.",
      benefits: [
        "Complete tree removal including stump",
        "Crane services for difficult access",
        "Protection mats to preserve your lawn",
        "Debris removal and site cleanup",
        "Emergency removal services available 24/7"
      ],
      process: [
        "Free on-site assessment and quote",
        "Safety planning and permit handling",
        "Professional removal with proper equipment",
        "Complete cleanup and disposal",
        "Final inspection and customer walkthrough"
      ],
      image: "https://images.unsplash.com/photo-1765064520245-2baac5e82689?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0cmVlJTIwcmVtb3ZhbHxlbnwwfHx8fDE3NzM5NTI1ODZ8MA&ixlib=rb-4.1.0&q=85"
    },
    2: {
      fullDescription: "Regular tree trimming and pruning is essential for tree health, safety, and aesthetics. Our certified arborists understand proper pruning techniques that promote healthy growth while maintaining the natural beauty of your trees.",
      benefits: [
        "Promotes healthy tree growth",
        "Removes dead or diseased branches",
        "Improves tree structure and appearance",
        "Prevents storm damage",
        "Increases property value"
      ],
      process: [
        "Tree health assessment",
        "Customized pruning plan",
        "Professional trimming by certified arborists",
        "Branch removal and cleanup",
        "Follow-up care recommendations"
      ],
      image: "https://images.unsplash.com/photo-1680124744736-859f16257ef0"
    },
    3: {
      fullDescription: "When storms strike or trees become hazardous, our 24/7 emergency response team is ready to help. We prioritize safety and quick response to protect your property and family from dangerous tree situations.",
      benefits: [
        "24/7 emergency response",
        "Rapid assessment and action",
        "Insurance claim assistance",
        "Hazard mitigation",
        "Professional storm cleanup"
      ],
      process: [
        "Immediate response to your call",
        "Safety assessment of the situation",
        "Emergency stabilization or removal",
        "Complete debris cleanup",
        "Documentation for insurance"
      ],
      image: "https://images.unsplash.com/photo-1591497108596-436c1a1a5c8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxzdG9ybSUyMGRhbWFnZXxlbnwwfHx8fDE3NzM5NTI3NzR8MA&ixlib=rb-4.1.0&q=85"
    },
    4: {
      fullDescription: "Some tree removals require specialized expertise and equipment. Our technical removal services handle challenging situations including trees near power lines, structures, or in confined spaces.",
      benefits: [
        "Expert handling of difficult removals",
        "Specialized rigging equipment",
        "Coordination with utility companies",
        "Minimal impact on surroundings",
        "Licensed and insured professionals"
      ],
      process: [
        "Detailed site evaluation",
        "Technical planning and permits",
        "Specialized equipment deployment",
        "Precision removal execution",
        "Thorough site restoration"
      ],
      image: "https://images.unsplash.com/photo-1754322449005-bdc38c631682"
    },
    5: {
      fullDescription: "Don't let unsightly stumps take up valuable yard space. Our stump grinding services completely remove stumps below ground level, allowing you to reclaim your property for landscaping or construction.",
      benefits: [
        "Complete stump removal",
        "Ground to 6-12 inches below surface",
        "Prevents regrowth",
        "Eliminates tripping hazards",
        "Creates usable space"
      ],
      process: [
        "Stump assessment and quote",
        "Professional grinding equipment",
        "Complete grinding below ground",
        "Wood chip removal or mulching",
        "Site leveling and cleanup"
      ],
      image: "https://images.unsplash.com/photo-1474742509976-ddec6b387356?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjaGFpbnNhd3xlbnwwfHx8fDE3NzM5NTI3NDl8MA&ixlib=rb-4.1.0&q=85"
    },
    6: {
      fullDescription: "Transform your outdoor space with our comprehensive landscaping services. From design to installation, we create beautiful, functional landscapes that enhance your property's curb appeal and value.",
      benefits: [
        "Custom landscape design",
        "Professional installation",
        "Plant selection and placement",
        "Hardscape integration",
        "Seasonal maintenance plans"
      ],
      process: [
        "Initial consultation and vision",
        "Design proposal and planning",
        "Material selection",
        "Professional installation",
        "Ongoing maintenance support"
      ],
      image: "https://images.unsplash.com/photo-1597201278257-3687be27d954?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxsYW5kc2NhcGluZ3xlbnwwfHx8fDE3NzM5NTI3MDd8MA&ixlib=rb-4.1.0&q=85"
    },
    7: {
      fullDescription: "Maintain the health and beauty of your trees with our ongoing arbor care services. Regular maintenance prevents problems and keeps your trees thriving for years to come.",
      benefits: [
        "Preventive tree health care",
        "Disease and pest management",
        "Fertilization programs",
        "Regular monitoring",
        "Long-term tree health"
      ],
      process: [
        "Initial tree health evaluation",
        "Customized care plan",
        "Regular maintenance visits",
        "Health monitoring and adjustments",
        "Seasonal care recommendations"
      ],
      image: "https://images.unsplash.com/photo-1626828476637-5bd713ef9f22"
    },
    8: {
      fullDescription: "Keep your lawn looking pristine with our professional grass cutting and lawn care services. We provide regular mowing, edging, trimming, and maintenance to ensure your yard stays healthy and beautiful year-round.",
      benefits: [
        "Professional lawn mowing and edging",
        "Consistent cutting height for healthy grass",
        "Debris cleanup and removal",
        "Flexible scheduling (weekly, bi-weekly, monthly)",
        "Seasonal lawn care recommendations",
        "Weed eating and trim work"
      ],
      process: [
        "Property assessment and quote",
        "Customized mowing schedule",
        "Professional cutting and edging",
        "Clipping removal or mulching",
        "Quality inspection and touch-ups"
      ],
      image: "https://images.unsplash.com/photo-1558904541-efa843a96f01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxsYXduJTIwbW93aW5nfGVufDB8fHx8MTczNzk1Mjc4Nnww&ixlib=rb-4.1.0&q=85"
    }
  };

  const details = serviceDetails[service.id] || serviceDetails[1];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={details.image}
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40 flex items-center">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {service.title}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl">
              {service.description}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {details.fullDescription}
              </p>
            </div>

            {/* Benefits */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Benefits</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {details.benefits.map((benefit, index) => (
                  <Card key={index} className="border-2 border-green-100">
                    <CardContent className="p-4 flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-700">{benefit}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Process */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Process</h2>
              <div className="space-y-4">
                {details.process.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-green-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-700">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="border-2 border-green-500 shadow-lg sticky top-28">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Started Today</h3>
                <p className="text-gray-600 mb-6">
                  Contact us for a free estimate and consultation
                </p>
                <div className="space-y-4">
                  <a href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}>
                    <Button className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-6 text-lg">
                      <Phone className="w-5 h-5 mr-2" />
                      Call {companyInfo.phone}
                    </Button>
                  </a>
                  <Button
                    onClick={() => {
                      navigate('/');
                      setTimeout(() => {
                        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    variant="outline"
                    className="w-full border-2 border-green-500 text-green-700 hover:bg-green-50 font-bold py-6 text-lg"
                  >
                    Request Free Estimate
                  </Button>
                </div>
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 text-center">
                    <CheckCircle className="w-4 h-4 inline text-green-500 mr-1" />
                    Licensed & Insured
                  </p>
                  <p className="text-sm text-gray-600 text-center mt-2">
                    <CheckCircle className="w-4 h-4 inline text-green-500 mr-1" />
                    24/7 Emergency Tree Service
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Other Services */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Other Services</h3>
                <div className="space-y-3">
                  {services
                    .filter(s => s.id !== service.id)
                    .slice(0, 4)
                    .map(s => (
                      <button
                        key={s.id}
                        onClick={() => navigate(`/services/${s.id}`)}
                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <p className="font-semibold text-gray-900">{s.title}</p>
                      </button>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
